import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Mail, Lock } from "lucide-react";
import UserService from "../services/UserService";
import bgImage from "../assets/images/login-bg.png";
import logoNSSA from "../assets/images/logo_nssa.png";
import logoSUBEBlanco from "../assets/images/logo_sube_blanco.svg";

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 16px",
    position: "relative",
    backgroundImage: `url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  overlay: {
    position: "absolute",
    inset: 0,
    // backgroundColor: "rgba(0, 71, 124, 0.60)",
  },
  glassCard: {
    position: "relative",
    zIndex: 10,
    width: "100%",
    maxWidth: "560px",
    borderRadius: "16px",
    padding: "32px 56px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "24px",
    background: "rgba(255,255,255,0.08)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(20px)",
    border: "1px solid rgba(255,255,255,0.20)",
    boxShadow: "0 8px 48px rgba(0,0,0,0.40)",
  },
  whiteCard: {
    width: "100%",
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "24px 28px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  label: {
    display: "block",
    fontSize: "0.875rem",
    fontWeight: 500,
    color: "#4B5563",
    marginBottom: "4px",
  },
  inputWrapper: {
    position: "relative",
  },
  iconLeft: {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#9CA3AF",
    pointerEvents: "none",
  },
  input: {
    width: "100%",
    paddingLeft: "36px",
    paddingRight: "16px",
    paddingTop: "10px",
    paddingBottom: "10px",
    borderRadius: "8px",
    border: "1px solid #E5E7EB",
    backgroundColor: "#F9FAFB",
    fontSize: "0.875rem",
    color: "#1F2937",
    outline: "none",
    boxSizing: "border-box",
  },
  inputPassword: {
    paddingRight: "40px",
  },
  eyeButton: {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    color: "#9CA3AF",
    display: "flex",
    alignItems: "center",
  },
  forgotLink: {
    display: "flex",
    justifyContent: "flex-end",
  },
  forgotBtn: {
    background: "none",
    border: "none",
    padding: 0,
    fontSize: "0.75rem",
    color: "#3080C2",
    cursor: "pointer",
    textDecoration: "none",
  },
  submitBtn: {
    width: "100%",
    padding: "10px",
    borderRadius: "8px",
    border: "none",
    backgroundColor: "#00603D",
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.875rem",
    letterSpacing: "0.05em",
    cursor: "pointer",
    boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
  },
  submitBtnDisabled: {
    backgroundColor: "#6B7280",
    cursor: "not-allowed",
  },
  errorBox: {
    fontSize: "0.8rem",
    color: "#B91C1C",
    backgroundColor: "#FEF2F2",
    border: "1px solid #FECACA",
    borderRadius: "6px",
    padding: "8px 12px",
    textAlign: "center",
  },
};

export default function Login() {
  const navigate = useNavigate();
  const [usuario, setUsuario] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await UserService.login(usuario, password);
      navigate("/");
    } catch (err) {
      setError(err.message || "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => navigate("/forgot-password");

  return (
    <div style={styles.page}>
      <div style={styles.overlay} />

      <div style={styles.glassCard} className="login-glass-card">
        <img src={logoNSSA} alt="Nación Servicios" style={{ height: "calc(var(--spacing) * 10)", width: "auto" }} />

        <div style={styles.whiteCard}>
          <form onSubmit={handleSubmit} style={styles.form}>
            <div>
              <label htmlFor="login-usuario" style={styles.label}>
                Usuario o email
              </label>
              <div style={styles.inputWrapper}>
                <span style={styles.iconLeft}>
                  <Mail size={16} aria-hidden="true" />
                </span>
                <input
                  id="login-usuario"
                  type="text"
                  value={usuario}
                  onChange={(e) => setUsuario(e.target.value)}
                  placeholder="Ingrese su usuario o email"
                  style={styles.input}
                  autoComplete="username"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="login-password" style={styles.label}>
                Contraseña
              </label>
              <div style={styles.inputWrapper}>
                <span style={styles.iconLeft}>
                  <Lock size={16} aria-hidden="true" />
                </span>
                <input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Ingrese su contraseña"
                  style={{ ...styles.input, ...styles.inputPassword }}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  style={styles.eyeButton}
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <div style={styles.forgotLink}>
              <button type="button" onClick={handleForgotPassword} style={styles.forgotBtn}>
                Olvidé mi clave
              </button>
            </div>

            {error && <div style={styles.errorBox}>{error}</div>}

            <button
              type="submit"
              disabled={loading}
              style={loading ? { ...styles.submitBtn, ...styles.submitBtnDisabled } : styles.submitBtn}
            >
              {loading ? "Iniciando sesión..." : "Iniciar sesión"}
            </button>
          </form>
        </div>

        <img src={logoSUBEBlanco} alt="SUBE" style={{ height: "40px", width: "auto" }} />
      </div>
    </div>
  );
}
